import React, { useState, useEffect } from 'react';
import { Plus, Loader2, Save, X, ToggleLeft, ToggleRight, Megaphone, Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';
import { resolveImageUrl } from '../../utils/resolveImageUrl';
import {
    CAMPANHA_VAZIA, paraInput, contadorSemFim, montaCorpoCampanha,
    BANNER_DICA, fusoLocal, detalheCampanha,
} from '../../utils/promoForm';
import CountdownTimer from '../../components/CountdownTimer';

/**
 * Admin da Central de Promoções — campanhas promocionais.
 *
 * Segue o padrão das outras páginas de `/sistema`: lista, formulário inline,
 * sem editor visual. O upload de imagem reaproveita `/api/banners/upload-image`,
 * que já existe e já grava em `uploads/banners`. Criar um segundo mecanismo de
 * upload só para campanhas não traria nada.
 *
 * O `status` mostrado não é um campo do banco: vem calculado da janela de datas.
 * Por isso uma campanha vencida aparece como `expired` aqui e some da vitrine
 * sozinha, sem job de expiração.
 */

const STATUS_ROTULO = {
    active: { texto: 'No ar', cor: 'var(--status-green)', fundo: 'var(--status-success-bg)' },
    scheduled: { texto: 'Agendada', cor: 'var(--status-blue)', fundo: 'var(--status-info-bg)' },
    expired: { texto: 'Encerrada', cor: 'var(--muted-2)', fundo: 'var(--surface-2)' },
    disabled: { texto: 'Desativada', cor: 'var(--muted-2)', fundo: 'var(--surface-2)' },
};

const TARGETS = [
    { key: 'internal', label: 'Página interna', ajuda: 'Rota do Super Vitrine, começando com /' },
    { key: 'deal', label: 'Produto (oferta)', ajuda: 'deal_id — o clique passa pelo redirect /r/' },
    { key: 'marketplace', label: 'Marketplace', ajuda: 'slug do marketplace — leva ao catálogo filtrado' },
];

const FUSO_LOCAL = fusoLocal();

export default function PromotionsPage() {
    const [campanhas, setCampanhas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [editando, setEditando] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const [marketplaces, setMarketplaces] = useState([]);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    // Campanha nova nasce DESATIVADA, ver utils/promoForm.js. Editar campanha
    // existente respeita o valor salvo, porque o formulario e preenchido com o
    // proprio documento vindo do servidor.
    const vazia = CAMPANHA_VAZIA;

    const buscar = () => {
        setCarregando(true);
        fetch(`${API}/promo-campaigns/admin`, { headers })
            .then((r) => r.json())
            .then((d) => setCampanhas(Array.isArray(d.campaigns) ? d.campaigns : []))
            .catch(() => toast.error('Erro ao carregar campanhas'))
            .finally(() => setCarregando(false));
    };

    useEffect(() => {
        buscar();
        fetch(`${API}/marketplaces/providers`, { headers })
            .then((r) => (r.ok ? r.json() : { providers: [] }))
            .then((d) => setMarketplaces(d.providers || []))
            .catch(() => setMarketplaces([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const faltaFimParaContador = contadorSemFim(editando);

    const salvar = async () => {
        if (!editando.title.trim()) { toast.error('Título é obrigatório'); return; }
        if (faltaFimParaContador) {
            toast.error('Contador precisa de data de fim. Informe o Fim ou desmarque "Mostrar contador".');
            return;
        }
        setSalvando(true);
        const novo = !editando.campaign_id;
        // Lista branca de campos, ver utils/promoForm.js: `campaign_id`, `status`,
        // `target_url`, `marketplace_name`, `created_at` e `updated_at` sao
        // calculados pelo backend e nunca voltam no corpo.
        const corpo = montaCorpoCampanha(editando);
        try {
            const r = await fetch(
                novo ? `${API}/promo-campaigns` : `${API}/promo-campaigns/${editando.campaign_id}`,
                { method: novo ? 'POST' : 'PATCH', headers, body: JSON.stringify(corpo) });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || 'Falha ao salvar');
            }
            toast.success(novo ? 'Campanha criada' : 'Campanha atualizada');
            setEditando(null);
            buscar();
        } catch (e) {
            toast.error(String(e.message || e));
        } finally {
            setSalvando(false);
        }
    };

    const alternarAtiva = async (c) => {
        try {
            const r = await fetch(`${API}/promo-campaigns/${c.campaign_id}`, {
                method: 'PATCH', headers, body: JSON.stringify({ active: !c.active }),
            });
            if (!r.ok) throw new Error('Falha ao alternar');
            buscar();
        } catch { toast.error('Não consegui alternar a campanha'); }
    };

    const enviarImagem = async (arquivo) => {
        const fd = new FormData();
        fd.append('file', arquivo);
        try {
            // Reaproveita o upload que já serve os banners.
            const r = await fetch(`${API}/banners/upload-image`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
            });
            const d = await r.json();
            if (!r.ok || !d.url) throw new Error(d.detail || 'Falha no upload');
            setEditando((e) => ({ ...e, banner_image: d.url }));
            toast.success('Imagem enviada');
        } catch (e) { toast.error(String(e.message || e)); }
    };

    const campo = 'w-full px-3 py-2 rounded-lg text-sm';
    const estiloCampo = { background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' };
    const rotulo = 'block text-[11px] mb-1 font-medium';

    return (
        <div className="p-4 sm:p-6" data-testid="promotions-page">
            <div className="flex items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-2">
                    <Megaphone size={18} style={{ color: 'var(--gold-2)' }} />
                    <h1 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Promoções</h1>
                </div>
                <button onClick={() => setEditando({ ...vazia })}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
                    style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
                    data-testid="nova-campanha">
                    <Plus size={14} /> Nova campanha
                </button>
            </div>

            {editando && (
                <div className="rounded-xl p-4 mb-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                            <label className={rotulo} style={{ color: 'var(--muted)' }}>Título</label>
                            <input className={campo} style={estiloCampo} value={editando.title}
                                onChange={(e) => setEditando({ ...editando, title: e.target.value })} />
                        </div>
                        <div className="sm:col-span-2">
                            <label className={rotulo} style={{ color: 'var(--muted)' }}>Subtítulo</label>
                            <input className={campo} style={estiloCampo} value={editando.subtitle}
                                onChange={(e) => setEditando({ ...editando, subtitle: e.target.value })} />
                        </div>
                        <div>
                            <label className={rotulo} style={{ color: 'var(--muted)' }}>Marketplace</label>
                            <select className={campo} style={estiloCampo} value={editando.marketplace}
                                onChange={(e) => setEditando({ ...editando, marketplace: e.target.value })}>
                                <option value="">Nenhum</option>
                                {marketplaces.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className={rotulo} style={{ color: 'var(--muted)' }}>Prioridade</label>
                            <input type="number" className={campo} style={estiloCampo} value={editando.priority}
                                onChange={(e) => setEditando({ ...editando, priority: e.target.value })} />
                            <p className="text-[10px] mt-1" style={{ color: 'var(--muted-2)' }}>
                                Maior número aparece primeiro.
                            </p>
                        </div>
                        <div>
                            <label className={rotulo} style={{ color: 'var(--muted)' }}>Início</label>
                            <input type="datetime-local" className={campo} style={estiloCampo}
                                value={paraInput(editando.starts_at)}
                                onChange={(e) => setEditando({ ...editando, starts_at: e.target.value })} />
                        </div>
                        <div>
                            <label className={rotulo} style={{ color: 'var(--muted)' }}>Fim</label>
                            <input type="datetime-local" className={campo} style={estiloCampo}
                                value={paraInput(editando.ends_at)}
                                onChange={(e) => setEditando({ ...editando, ends_at: e.target.value })} />
                        </div>
                        <div className="sm:col-span-2 -mt-1">
                            <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>
                                Você informa no seu horário local ({FUSO_LOCAL}). O sistema guarda em UTC.
                            </p>
                        </div>
                        <div>
                            <label className={rotulo} style={{ color: 'var(--muted)' }}>Destino</label>
                            <select className={campo} style={estiloCampo} value={editando.target_type}
                                onChange={(e) => setEditando({ ...editando, target_type: e.target.value })}>
                                {TARGETS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className={rotulo} style={{ color: 'var(--muted)' }}>
                                {TARGETS.find((t) => t.key === editando.target_type)?.ajuda}
                            </label>
                            <input className={campo} style={estiloCampo} value={editando.target_value}
                                onChange={(e) => setEditando({ ...editando, target_value: e.target.value })} />
                        </div>
                        <div className="sm:col-span-2">
                            <label className={rotulo} style={{ color: 'var(--muted)' }}>Imagem do banner</label>
                            <div className="flex items-center gap-2">
                                <input className={campo} style={estiloCampo} value={editando.banner_image}
                                    placeholder="/api/uploads/banners/arquivo.png"
                                    onChange={(e) => setEditando({ ...editando, banner_image: e.target.value })} />
                                <label className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs cursor-pointer"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                                    <Upload size={13} /> Enviar
                                    <input type="file" accept="image/*" className="hidden"
                                        onChange={(e) => e.target.files?.[0] && enviarImagem(e.target.files[0])} />
                                </label>
                            </div>
                            <p className="text-[10px] mt-1" style={{ color: 'var(--muted-2)' }}>
                                {BANNER_DICA}
                            </p>
                            {/* Preview na proporcao real do card da Home, com o mesmo
                                object-cover: o admin ve o recorte que o visitante vera. */}
                            {editando.banner_image && (
                                <div className="mt-2">
                                    <img
                                        src={resolveImageUrl(editando.banner_image)}
                                        alt="Prévia do banner"
                                        className="rounded-lg object-cover"
                                        style={{ width: 228, height: 61, border: '1px solid var(--border)' }}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        onLoad={(e) => { e.currentTarget.style.display = ''; }}
                                        data-testid="banner-preview"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-4 flex-wrap">
                            <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--text)' }}>
                                <input type="checkbox" checked={!!editando.active}
                                    onChange={(e) => setEditando({ ...editando, active: e.target.checked })}
                                    data-testid="campo-ativa" />
                                Ativa
                            </label>
                            <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--text)' }}>
                                <input type="checkbox" checked={!!editando.show_countdown}
                                    onChange={(e) => setEditando({ ...editando, show_countdown: e.target.checked })} />
                                Mostrar contador
                            </label>
                            {faltaFimParaContador && (
                                <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg"
                                    style={{ background: 'rgba(248,113,113,0.12)', color: '#f87171' }}
                                    data-testid="erro-contador-sem-fim">
                                    <AlertTriangle size={12} />
                                    Informe a data de <strong>Fim</strong> ou desmarque o contador.
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <button onClick={salvar} disabled={salvando || faltaFimParaContador}
                            title={faltaFimParaContador ? 'O contador precisa de data de fim' : undefined}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
                            data-testid="salvar-campanha">
                            {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
                        </button>
                        {/* Cancelar descarta o rascunho fechando o formulario: o estado
                            de edicao volta a null e a lista continua a do servidor. */}
                        <button onClick={() => setEditando(null)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs"
                            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}
                            data-testid="cancelar-campanha">
                            <X size={14} /> Cancelar
                        </button>
                    </div>
                </div>
            )}

            {carregando ? (
                <div className="flex justify-center py-10"><Loader2 className="animate-spin" style={{ color: 'var(--muted)' }} /></div>
            ) : campanhas.length === 0 ? (
                <p className="text-sm py-10 text-center" style={{ color: 'var(--muted)' }}>
                    Nenhuma campanha cadastrada ainda.
                </p>
            ) : (
                <div className="space-y-2">
                    {campanhas.map((c) => {
                        const st = STATUS_ROTULO[c.status] || STATUS_ROTULO.disabled;
                        return (
                            <div key={c.campaign_id}
                                className="flex items-center gap-3 p-3 rounded-xl"
                                style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
                                data-testid="campanha-linha">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{c.title}</p>
                                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                                            style={{ background: st.fundo, color: st.cor }}>{st.texto}</span>
                                        {c.show_countdown && c.ends_at && c.status === 'active' && (
                                            <CountdownTimer endsAt={c.ends_at} compacto />
                                        )}
                                    </div>
                                    <p className="text-[11px] mt-0.5 truncate" style={{ color: 'var(--muted-2)' }}>
                                        {detalheCampanha(c)}
                                    </p>
                                </div>
                                <button onClick={() => alternarAtiva(c)} title={c.active ? 'Desativar' : 'Ativar'}>
                                    {c.active
                                        ? <ToggleRight size={22} style={{ color: 'var(--gold-2)' }} />
                                        : <ToggleLeft size={22} style={{ color: 'var(--muted-2)' }} />}
                                </button>
                                <button onClick={() => setEditando({ ...c })}
                                    className="px-3 py-1.5 rounded-lg text-xs"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                                    Editar
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
